import React from 'react';
import './burgerMenuStyle.css';
import routes from '../routes/routes';
import NavbarHeader from './NavbarHeader';
import DashLogo from '../../assets/svgs/LogoLongText.svg';
import {useTagsStore} from '../../common/stores/useTagsStore';
import {useUiStore} from '../../common/stores/useUiStore';
import {useEventDetailsStore, useEventDtoStore} from '../../common';

export interface MenuLink {
  label: string;
  to: string;
}

export interface MenuGroup {
  title: string;
  links: MenuLink[];
  // When true, navigating a link in this group also clears the event stores.
  clearOnNavigate?: boolean;
}

const defaultGroups: MenuGroup[] = [
  {
    title: 'Places',
    links: [
      {label: 'Get All Places', to: routes.getAllPlaces},
      {label: 'Create Place', to: routes.createPlace},
      {label: 'Approve Places', to: routes.approvePlaces},
    ],
  },
  {
    title: 'Events',
    links: [{label: 'All Events', to: routes.getAllEvents}],
    clearOnNavigate: true,
  },
  {
    title: 'Admin',
    links: [
      {label: 'All Activities', to: routes.activities},
      {label: 'Users', to: routes.users},
      {label: 'Support', to: routes.support},
      {label: 'Tokens', to: routes.tokens},
      {label: 'Challenges', to: routes.challenges},
      {label: 'Approve Business', to: routes.approveBusiness},
    ],
  },
];

// Matches the breakpoint in burgerMenuStyle.css: below it the sidebar covers
// the page, so it should get out of the way once a link is chosen.
const isNarrowScreen = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(max-width: 899px)').matches;

interface BurgerMenuProps {
  groups?: MenuGroup[];
}

export default function BurgerMenu({groups = defaultGroups}: BurgerMenuProps) {
  const isOpen = useUiStore(state => state.isNavOpen);
  const setIsOpen = useUiStore(state => state.setIsNavOpen);
  const {clearSelectedTagCodes} = useTagsStore();
  const {clearEventDto} = useEventDtoStore();
  const {clearEventDetails} = useEventDetailsStore();

  const handleNavigate = (): void => {
    if (isNarrowScreen()) {
      setIsOpen(false);
    }
  };

  const handleNavigateWithClear = () => {
    handleNavigate();
    clearSelectedTagCodes();
    clearEventDto();
    clearEventDetails();
  };

  return (
    <nav className="burger-menu" aria-label="Main navigation">
      {!isOpen && (
        <button
          className="burger"
          onClick={() => setIsOpen(true)}
          aria-label="Open navigation"
          aria-expanded={false}>
          <span></span>
          <span></span>
          <span></span>
        </button>
      )}

      <div
        className={`sidebar-backdrop ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(false)}
      />

      <div className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <img src={DashLogo} alt="DashOut" className="sidebar-logo" />
          <button
            className="sidebar-collapse"
            onClick={() => setIsOpen(false)}
            aria-label="Collapse navigation">
            «
          </button>
        </div>

        {groups.map(group => {
          const onLinkClick = group.clearOnNavigate
            ? handleNavigateWithClear
            : handleNavigate;
          const subHeaders = group.links.map(link => ({
            ...link,
            onClick: onLinkClick,
          }));

          return (
            <NavbarHeader
              key={group.title}
              headerTitle={group.title}
              subHeaders={subHeaders}
              onLinkClick={onLinkClick}
              headerClassName="menu-group-title"
              subHeaderClassName="menu-sub-link"
            />
          );
        })}
      </div>
    </nav>
  );
}
